import React, { createContext, useContext } from 'react';

/**
 * The card primitive — the only surface recipe in the console.
 *
 * A surface is defined by a hairline border, never a shadow, gradient or blur:
 * `rounded-lg border border-line bg-surface`. Anything that floats (menu, modal)
 * is a different component with `shadow-pop` / `shadow-modal`.
 */

const cx = (...parts) => parts.filter(Boolean).join(' ');

/**
 * Whether the enclosing Card carries its own `p-card`.
 *
 * CardFooter needs this because a footer must reach the card's left and right
 * edges to look like a footer. Inside a padded card it cancels that padding and
 * re-applies it internally; inside `padded={false}` (a card wrapping a table,
 * which must sit edge to edge) it supplies the padding itself. Reading it from
 * context keeps the caller from having to repeat `padded` on the footer, and a
 * stray CardFooter defaults to the padded case because that is the common card.
 */
const CardPadContext = createContext(true);

/**
 * @param {object} props
 * @param {string|Function} props.as  element or component to render as — 'section'
 *   and 'article' are worth reaching for when the card is a real page landmark.
 * @param {boolean} props.padded  false for cards whose content is edge to edge
 *   (DataTable, Heatmap); pad the header and footer regions instead.
 */
export function Card({ as = 'div', className = '', padded = true, children, ...rest }) {
  const Tag = as;
  return (
    <CardPadContext.Provider value={padded}>
      <Tag
        className={cx('rounded-lg border border-line bg-surface', padded && 'p-card', className)}
        {...rest}
      >
        {children}
      </Tag>
    </CardPadContext.Provider>
  );
}

/**
 * Title + subtitle on the left, meta + actions on the right.
 *
 * @param {object} props
 * @param {string|React.ReactNode} props.title
 * @param {string} props.subtitle  the question this card answers, in plain words
 * @param {string|React.ReactNode} props.meta  right-aligned figures — a row count,
 *   a coverage note, "as of 14:32". Monospaced so it does not jitter on refetch.
 * @param {React.ReactNode} props.actions
 */
export function CardHeader({ title, subtitle, meta, actions, className = '' }) {
  const hasSide = meta != null || actions != null;

  return (
    // The left block asks for 15rem and the row gap is 1.5rem, so the side block
    // drops onto its own line somewhere around 420px rather than crushing the
    // title into two-character-wide columns.
    <div
      className={cx(
        'mb-gutter flex flex-wrap items-start justify-between gap-x-block gap-y-gutter',
        className
      )}
    >
      {(title != null || subtitle != null) && (
        <div className="min-w-0 flex-1 basis-[15rem]">
          {title != null && (
            <h3 className="text-title-sm font-semibold text-ink-900">{title}</h3>
          )}
          {subtitle != null && (
            <p className="mt-0.5 text-label text-ink-500">{subtitle}</p>
          )}
        </div>
      )}

      {hasSide && (
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-gutter">
          {meta != null && (
            <span className="whitespace-nowrap font-mono text-micro tabular-nums text-ink-500">
              {meta}
            </span>
          )}
          {actions != null && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * A hairline-separated strip at the bottom of a card: provenance, a link to the
 * full list, "updated 4 min ago". Not a place for primary actions.
 */
export function CardFooter({ children, className = '' }) {
  const padded = useContext(CardPadContext);

  return (
    <div
      className={cx(
        'border-t border-line px-card py-gutter text-micro text-ink-500',
        // Cancel the card's own padding so the rule spans the full width and the
        // strip sits flush against the bottom edge.
        padded && '-mx-card -mb-card mt-card',
        className
      )}
    >
      {children}
    </div>
  );
}
